import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {Box, Checkbox, FormControl, IconButton, InputAdornment, InputBase} from '@mui/material'
import {Button, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useRef, useState} from 'react'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import AppVersion from 'src/components/AppVersion'
import {useAuth} from 'src/contexts/AuthContext'
import usePersonalizedThemeByCompany, {CompaniesWithPersonalizedDashboards} from 'src/hooks/usePersonalizedThemeByCompany'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {getDecodedToken} from 'src/utilities/getDecodedToken'
import {isEmailLoginValid, isPasswordLoginValid} from 'src/utilities/regexValidation'
import useLoading from '../../hooks/useLoading'
import BrainLoginThumb from '../../images/brain-login-image.png'
import BrainLoginVideo from '../../images/brain-login-video.mp4'
import IconLogin from '../../images/iconLogin.svg'
import api from '../../services/api'
import {AxiosErrorGeneric} from '../../utilities/RequestError'
import handleErrorWithSnackbar from '../../utilities/handleErrorWithSnackbar'
import {ScreenContainer} from '../password-change/Styles'
import {RedirectErrors} from './LoginFunctions'
import {
  BlendDiv,
  ButtonDiv,
  Container,
  ContainerButton,
  ContainerOptions,
  ContainerSSO,
  Form,
  Input,
  InputDiv,
  IncorrectUser as InvalidState,
  LinkText,
  LoginBox,
  Logo,
  MessageSpan,
  SSOButton,
  TopDiv,
  VideoContainer,
} from './Styles'
import BlockedUserModal from './components/BlockedUserModal'
import LogoutSSO from './components/logoutSSO/LogoutSSO'

function Login() {
  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [invalidUsername, setInvalidUsername] = useState<boolean>(false)
  const [invalidPassword, setInvalidPassword] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const {loading, applyLoading} = useLoading(false)
  const {login, authFromUrlToken, logout} = useAuth()
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [isSelected, setIsSelected] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const {loginPagesLogo} = usePersonalizedThemeByCompany()

  const navigate = useNavigate()
  const location = useLocation()
  const blockAuthRequests = useRef<boolean>(false)
  const queryParams = new URLSearchParams(location.search)
  const token = queryParams.get('otp')
  const redirectError = queryParams.get('error')
  const {enqueueSnackbar} = useSnackbar()

  const handleClickShowPassword = () => {
    setShowPassword((previous) => !previous)
  }
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  async function handleKeyPress(event: any) {
    if (event.key === 'Enter') {
      if (username.length > 0 && password.length > 0) await loginPost()
      else setMessage('Este campo é obrigatório.')
    }
  }

  async function returnB2cUserWhereHeLeftOff() {
    const decodedToken = getDecodedToken()
    const authorizedCompanies = decodedToken.payload.authorizedCompanies

    const hasSelfRegistrationPermission = decodedToken.payload.userPermissions.includes('self-registration:create')
    if (!hasSelfRegistrationPermission) return navigate('/')

    const userHasMoreThanOneCompany = authorizedCompanies.length > 1
    if (userHasMoreThanOneCompany) return navigate('/')

    const companies = await api.company.getMany({filter: {ids: [authorizedCompanies[0]]}})

    const userAreLegalPerson = companies.data.data.entities[0].document.length >= 15
    if (userAreLegalPerson) return navigate('/status?type=company')

    const plan = await api.payments.getUserCurrentBilling({companyId: String(authorizedCompanies[0])})
    if (!plan.data.data) return navigate('/select-plan')

    const cameras = (
      await api.camera.getMany({
        page: 1,
        pageSize: 100,
        filter: {
          companyIds: [authorizedCompanies[0]],
        },
        includes: ['configuration'],
      })
    ).data.data.entities

    if (!cameras.length) return navigate('/camera-config')

    const objects = (await api.object.getAllObjectsOnCamera({cameraId: cameras[0].id})).data.data
    if (!objects.length) return navigate('/camera-config')
    return navigate('/')
  }

  const nextParam = localStorage.getItem('redirectToAfterLogin')

  async function loginPost() {
    if (loading) return
    if (!isEmailLoginValid(username)) {
      setMessage('Insira um e-mail válido')
      setInvalidUsername(true)
    } else if (!isPasswordLoginValid(password)) {
      setMessage('A senha deve conter no mínimo 8 e no máximo 40 caracteres, e deve conter um número e uma letra')
      setInvalidPassword(true)
    } else if (username.length > 0 && password.length > 0) {
      setMessage('')
      try {
        await applyLoading(async () => await login(username, password, isSelected))
        returnB2cUserWhereHeLeftOff()

        if (nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')) {
          localStorage.removeItem('redirectToAfterLogin')
          navigate(nextParam)
        }
      } catch (error: any) {
        if (error.response.status === 428) return setIsOpen(true)
        const err = error as AxiosErrorGeneric
        handleErrorWithSnackbar(enqueueSnackbar, err, 'Não foi possível fazer login')
      }
    }
  }

  useEffect(() => {
    if (invalidUsername && isEmailLoginValid(username)) {
      setMessage('')
      setInvalidUsername(false)
    }
  }, [username, invalidUsername])

  useEffect(() => {
    if (invalidPassword && isPasswordLoginValid(password)) {
      setMessage('')
      setInvalidPassword(false)
    }
  }, [password, invalidPassword])

  const label = {inputProps: {'aria-label': 'Checkbox demo'}}

  const redirectSSO = () => {
    window.location.href = `${process.env.REACT_APP_AUTH_BASE_URL}/sso/msal/login`
  }

  async function authenticateSSO() {
    if (blockAuthRequests.current) return
    blockAuthRequests.current = true
    if (token && !redirectError) {
      logout()
      const response = await authFromUrlToken(token)
      if (response) navigate('/')
      else {
        navigate('/login')
        setMessage('Não foi possível fazer login')
      }
    }
    blockAuthRequests.current = false
  }

  useEffect(() => {
    authenticateSSO()
  }, [token, redirectError])

  const url = window.location.href

  return (
    <ScreenContainer>
      <BlendDiv />
      <Container>
        <BlockedUserModal isOpen={isOpen} setIsOpen={setIsOpen} email={username} />

        {!redirectError ? (
          <LoginBox>
            <TopDiv>
              <Logo src={loginPagesLogo} />
              <MessageSpan color={'#F2F4F7'}>
                <b style={{fontWeight: 600}}>Bem-vindo</b> ao <b style={{fontWeight: 600}}>Brain</b>
                <br /> <b style={{fontWeight: 600}}>A inteligência</b> que <b style={{fontWeight: 600}}>protege</b>
              </MessageSpan>
            </TopDiv>
            {CompaniesWithPersonalizedDashboards.every((companyUrl) => !url.includes(companyUrl)) && (
              <>
                <ContainerSSO>
                  <SSOButton onClick={redirectSSO}>
                    <ContainerButton>
                      <img src={IconLogin} alt='microsoft icon' style={{paddingRight: '8px'}}></img>
                      <span>Entrar com minha conta Microsoft</span>
                    </ContainerButton>
                  </SSOButton>
                </ContainerSSO>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: '100%', gap: '36px'}}>
                  <Box sx={{width: '100%', borderTop: '1px solid #F2F4F7'}} />
                  <Box sx={{width: '24px', textAlign: 'center', marginBottom: '4px', background: 'transparent'}}>
                    <Typography variant='label' color='#F2F4F7'>
                      ou
                    </Typography>
                  </Box>
                  <Box sx={{width: '100%', borderTop: '1px solid #F2F4F7'}} />
                </Box>
              </>
            )}
            <Form
              onSubmit={async (event: any) => {
                event.preventDefault()
                await loginPost()
              }}>
              <InputDiv>
                <Box sx={{width: '100%', flexDirection: 'column', display: 'flex', flex: 1, gap: '8px'}}>
                  <Typography variant='label' htmlFor='username' color='#F2F4F7' size='16px'>
                    Login
                  </Typography>
                  <Input
                    id='username'
                    value={username}
                    onChange={(e: any) => setUsername(e.target.value.toLowerCase().trim())}
                    type={'text'}
                    autoFocus={true}
                    onKeyDown={async (event: any) => await handleKeyPress(event)}
                  />
                </Box>
                <Box sx={{width: '100%', flexDirection: 'column', display: 'flex', flex: 1, gap: '8px'}}>
                  <Typography variant='label' htmlFor='username' color='#F2F4F7' size='16px'>
                    Senha
                  </Typography>
                  <FormControl focused={false}>
                    <InputBase
                      sx={styles.password}
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onKeyDown={async (event) => await handleKeyPress(event)}
                      onChange={(e) => setPassword(e.target.value.trim())}
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton
                            sx={{color: '#FFFFFF'}}
                            aria-label='toggle password visibility'
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge='end'>
                            {showPassword === false ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                </Box>
              </InputDiv>
              <ContainerOptions>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                  <Checkbox {...label} id='keepConnected' onClick={() => setIsSelected((prev) => !prev)} value={isSelected} size='small' sx={styles.check} />
                  <Typography variant='label' htmlFor='keepConnected' color='#F2F4F7' size='14px'>
                    Manter-se conectado
                  </Typography>
                </Box>
                <Box>
                  <Link to='/forgot-password'>
                    <LinkText>Esqueci minha senha</LinkText>
                  </Link>
                </Box>
              </ContainerOptions>
              <Box>
                <InvalidState>{message}</InvalidState>
              </Box>
              <ButtonDiv>
                <Button isLoading={loading} type='submit' width='100%'>
                  Entrar
                </Button>
              </ButtonDiv>
            </Form>
          </LoginBox>
        ) : (
          <LogoutSSO errorOnSSOLogin={RedirectErrors[redirectError] ?? 'erro inesperado'} />
        )}
      </Container>
      <AppVersion color='white' />
      <VideoContainer muted autoPlay loop preload='auto' src={BrainLoginVideo} poster={BrainLoginThumb}></VideoContainer>
    </ScreenContainer>
  )
}
const styles = createStyleSheet({
  password: {
    fontSize: '16px',
    fontFamily: 'Inter, sans-serif',
    paddingLeft: '20px',
    paddingRight: '15px',
    color: '#eaecf0',
    backgroundColor: '#344054',
    height: '52px',
    borderRadius: '10px',
    border: 'none',
    textIndent: '4%',
    outline: 0,
    '@global': {
      'input:-webkit-autofill, input:-webkit-autofill:focus, input:-webkit-autofill:hover': {
        transition: 'background-color 5000s ease-in-out 0s',
        WebkitTextFillColor: '#eaecf0 !important',
        caretColor: '#eaecf0',
      },
    },
  },
  check: {
    color: 'white',
    padding: 0,
    margin: '10px 5px 10px 0px',
  },
})

export default Login
